import { lazy, Suspense, useMemo } from "react";
import { RouteObject, useRoutes } from "../../helpers/tixxt-router";
import { flatMap } from "lodash";
import CustomConfigurator from "../../CustomConfigurator";

import { UserbarContent } from "./Userbar";

const ActivitystreamsUserbar = lazy(
  () => import("../../components/activitystreams/Userbar"),
);
const MessagesUserbar = lazy(() => import("../../components/messages/Userbar"));
const AppointmentsUserbar = lazy(
  () => import("../../components/appointments/Userbar"),
);
const TasksUserbar = lazy(() => import("../../components/tasks/Userbar"));
const FilesUserbar = lazy(() => import("../../components/files/Userbar"));
const AdministrationUserbar = lazy(
  () => import("../../components/administration/Userbar"),
);
const InfoUserbar = lazy(() => import("../../components/info/Userbar"));
const SearchUserbar = lazy(
  () => import("../../components/globalSearch/Userbar"),
);
const MembershipsUserbar = lazy(
  () => import("../../components/memberships/Userbar"),
);
const DirectoryUserbar = lazy(
  () => import("../../components/directory/Userbar"),
);
const AppCreatorUserbar = lazy(
  () => import("../../components/appCreator/Userbar"),
);
const ChannelsUserbar = lazy(() => import("../../components/channels/Userbar"));
const ContactsUserbar = lazy(() => import("../../components/contacts/Userbar"));
const ChannelAdministrationUserbar = lazy(
  () => import("../../components/administration/channels/Userbar"),
);
const SupportUserbar = lazy(
  () => import("../../components/ai/support/Userbar"),
);
const KnowledgeAssistantUserbar = lazy(
  () => import("../../components/ai/knowledge_assistant/Userbar"),
);
/*
Expands the given rootPath into routes for network and group context as well as all subpaths (via wildcard)
 */
function expandRootPaths(
  rootPaths: string[],
  routeObject: Omit<RouteObject, "path">,
): RouteObject[] {
  return flatMap(rootPaths, (rootPath) => [
    { ...routeObject, path: rootPath + "/*" },
    { ...routeObject, path: "/groups/:groupSlug" + rootPath + "/*" },
  ]);
}

function moduleRoutes(): RouteObject[] {
  return [
    ...expandRootPaths(
      ["/activitystreams", "/activities", "/invites", "/notifications"],
      { element: <ActivitystreamsUserbar /> },
    ),
    ...expandRootPaths(["/messages"], { element: <MessagesUserbar /> }),
    ...expandRootPaths(["/appointments", "/calendars"], {
      element: <AppointmentsUserbar />,
    }),
    ...expandRootPaths(["/tasks"], { element: <TasksUserbar /> }),
    ...expandRootPaths(["/files"], { element: <FilesUserbar /> }),
    // contacts is a network-global module — no group context variant needed
    ...(Preload.current_network.availableModules?.includes("Contacts")
      ? [
          { path: "/contacts", element: <ContactsUserbar /> },
          { path: "/contacts/*", element: <ContactsUserbar /> },
        ]
      : []),
  ];
}

function extraRoutes(): RouteObject[] {
  return [
    { path: "/info", element: <InfoUserbar /> },
    { path: "/imprint", element: <InfoUserbar /> },
    { path: "/search", element: <SearchUserbar /> },
    { path: "/groups/:groupSlug/search", element: <SearchUserbar /> },
    { path: "/administration/*", element: <AdministrationUserbar /> },
    {
      path: "/groups/:groupSlug/administration/*",
      element: <AdministrationUserbar />,
    },
    {
      path: "/group-categories/:groupCategorySlug/administration/*",
      element: <AdministrationUserbar />,
    },
    {
      path: "/groups/:groupSlug/invites/sent",
      element: <AdministrationUserbar />,
    },
    {
      path: "/groups/:groupSlug/membership_invites/new",
      element: <DirectoryUserbar />,
    },
    {
      path: "/channels/:channelSlug/administration",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/edit",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/layout/edit",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/design/edit",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/widgets",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/widgets/*",
      element: <ChannelAdministrationUserbar />,
    },
    {
      path: "/channels/:channelSlug/administration/focus_labels",
      element: <ChannelAdministrationUserbar />,
    },

    { path: "/groups/:groupSlug/edit", element: <AdministrationUserbar /> },
    { path: "/invites/sent", element: <AdministrationUserbar /> },
    { path: "/invites/new", element: <AdministrationUserbar /> },
    { path: "/groups/:groupSlug/members", element: <DirectoryUserbar /> },
    { path: "/directory/channels", element: <ChannelsUserbar /> },
    { path: "/directory/:sectionSlug", element: <DirectoryUserbar /> },
    { path: "/members", element: <DirectoryUserbar /> },
    { path: "/map", element: <DirectoryUserbar map /> },
    { path: "/map/members", element: <DirectoryUserbar map /> },
    { path: "/groups/:groupSlug/map", element: <DirectoryUserbar map /> },
    { path: "/groups/:groupSlug", element: <ActivitystreamsUserbar /> },
    {
      path: "/groups/:groupSlug/integrations/*",
      element: <ActivitystreamsUserbar />,
    },
    { path: "/pages/:pageSlug", element: <AppCreatorUserbar /> },
    { path: "/pages/:pageSlug/*", element: <AppCreatorUserbar /> },
    { path: "/apps/:appSlug", element: <AppCreatorUserbar /> },
    { path: "/apps/:appSlug/*", element: <AppCreatorUserbar /> },
    {
      path: "/groups/:groupSlug/pages/:pageSlug",
      element: <ActivitystreamsUserbar />,
    },
    { path: "/members/:membershipId", element: <MembershipsUserbar /> },
    { path: "/members/:membershipId/*", element: <MembershipsUserbar /> },
    { path: "/users/edit", element: <MembershipsUserbar /> },
    { path: "/channels", element: <ChannelsUserbar /> },
    { path: "/channels/:channelSlug", element: <ChannelsUserbar /> },
    { path: "/channels/:channelSlug/*", element: <ChannelsUserbar /> },
    {
      path: "/blogs",
      element: <ChannelsUserbar />,
    },
    {
      path: "/blogs/:blogId/articles/:articleId",
      element: <ChannelsUserbar />,
    },
    {
      path: "/blogs/:blogId/articles/:articleId/*",
      element: <ChannelsUserbar />,
    },
    {
      path: "/blogs/:blogId/*",
      element: <ChannelsUserbar />,
    },
    ...(Preload.current_network.availableModules?.includes("Support")
      ? [
          { path: "/support", element: <SupportUserbar /> },
          { path: "/support/*", element: <SupportUserbar /> },
        ]
      : []),
    ...(Preload.current_member?.knowledge_assistant_available
      ? [
          {
            path: "/knowledge_assistant",
            element: <KnowledgeAssistantUserbar />,
          },
          {
            path: "/knowledge_assistant/*",
            element: <KnowledgeAssistantUserbar />,
          },
        ]
      : []),
    { path: "/users/sign_in", element: null },
    { path: "/users/password/new", element: null },
    { path: "/*", element: <InfoUserbar /> },
  ];
}

/*
This component is nothing more than routes, some of which are conditional, to the individual Userbars
 */
export default function TixxtUserbar() {
  const customUserbarRoutes: RouteObject[] =
    CustomConfigurator.get("userbarRoutes") || [];
  const routes = useMemo(
    () => [...customUserbarRoutes, ...moduleRoutes(), ...extraRoutes()],
    [customUserbarRoutes],
  );
  const element = useRoutes(routes);

  return (
    <UserbarContent id="userbar">
      <Suspense fallback={null}>{element}</Suspense>
    </UserbarContent>
  );
}
